/**
 * Senior Content Writer Workspace (Task 219 · backed by the live Task 220
 * backend) — the REVIEW home for `senior_content_writer`. A read/decide surface
 * over submitted content-writer drafts; NO editable authoring fields here.
 *
 * Assigned-only: `list-content-writer-drafts?status=submitted_to_senior` (RLS
 * gives the senior the in-scope review queue). Compare uses
 * `list-content-variants` for the brief + the canonical/AI source text.
 *
 * Task 292C2 — this is now the WRITING REVIEW LANE, reachable at
 * `/app/writing-review[/:draftId]` by senior_content_writer AND by owner /
 * marketing_manager as operational fallback reviewers. Before this task it
 * rendered ONLY as the `AppLanding` default for senior_content_writer, behind
 * `!isOwner && !isMarketingManager && ...`, so the two fallback reviewers were
 * guarded out of the only surface that could reach the RPC they are permitted
 * to call. It also had no id in the URL, so refresh and back/forward lost the
 * selection — the same defect QA-A fixed for the writer surface.
 *
 * Two decisions, not three: «اعتماد المحتوى» (approve_internal) and «طلب
 * تعديلات» (return, reason required). Approving hands the item to INTERNAL
 * review and never to the client — sending to the client stays the separate
 * explicit action on the content page, for owner / marketing_manager /
 * senior_content_writer / account_manager.
 *
 * RTL. Reuses the `.dwk-*` production-workspace layout.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingState, PermissionDeniedState } from '../components/states';
import { useMeContext } from '../context/MeContextProvider';
import { IdeaReviewCard, reviewItemFromContent } from './client/ContentPage';
import type {
  ContentWriterDraftRow,
  ListContentVariantsData,
  ListContentWriterDraftsData,
  MeContext,
} from '../api/types';
import {
  listContentVariants,
  listContentWriterDrafts,
  reviewContentWriterDraft,
} from '../api/endpoints';
import { avatarColor, initial, SOON } from './ownerCommandCenter.logic';
import {
  canOpenWritingReview,
  draftStatusLabelAr,
  isReviewable,
  isValidReviewReason,
  reviewAgeLabelAr,
  WRITING_REVIEW_APPROVE_AR,
  WRITING_REVIEW_APPROVED_NOTE_AR,
  WRITING_REVIEW_ARCHIVED_AR,
  WRITING_REVIEW_DENIED_AR,
  writingReviewEmptyAr,
  WRITING_REVIEW_LOADING_AR,
  WRITING_REVIEW_REASON_REQUIRED_AR,
  WRITING_REVIEW_REQUEST_CHANGES_AR,
  WRITING_REVIEW_STALE_AR,
  writingReviewHref,
  platformLabelAr,
  promotableComparison,
  resolveSelected,
} from './contentWriterWorkspace.logic';
import type { ContentVariantRow } from './client/productionBrief';
import {
  presentStaticBriefFields,
  presentVideoBriefFields,
  isVideoBriefPlatform,
} from './client/productionBrief';
import { useProductionClientSelection } from './useProductionClientSelection';

interface Props {
  readonly me: MeContext;
}

type QueueState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly drafts: ReadonlyArray<ContentWriterDraftRow> }
  | { readonly status: 'error' };

function actionErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : '';
  // Task 292C2 — the two server refusals a reviewer can actually hit get their
  // own Arabic wording instead of a forwarded backend string.
  //
  // The archived case is reachable even though QA-C excludes archived clients
  // from the QUEUE: a client can be archived while the reviewer already has the
  // draft open, and the RPC then refuses with `archived client is read-only`
  // (Task 292C1-QA-B). Telling the reviewer WHY is the difference between a
  // dead button and an explained one.
  if (raw.includes('archived client is read-only')) return WRITING_REVIEW_ARCHIVED_AR;
  if (raw.includes('not awaiting review')) return WRITING_REVIEW_STALE_AR;
  if (raw.trim().length > 0) return raw;
  return 'تعذّر تنفيذ المراجعة. حاول مرّة أخرى.';
}

/**
 * Task 292C2 — the routed entry for `/app/writing-review[/:draftId]`.
 *
 * `canOpenWritingReview` checks the reviewer role AND `content.writing.approve`
 * — the exact permission `review_content_writer_draft_v1` checks — so this gate
 * can never be more permissive than the server. It is presentation only: the
 * RPC re-derives role, permission, client scope, archived state and
 * self-review on every call, so a direct API call gains nothing by skipping it.
 */
export function WritingReviewRoute(): JSX.Element {
  const { me } = useMeContext();
  if (!me) return <LoadingState title="جاري التحميل" />;
  if (!canOpenWritingReview(me))
    return <PermissionDeniedState description={WRITING_REVIEW_DENIED_AR} />;
  return <SeniorContentWriterWorkspace me={me} />;
}

export function SeniorContentWriterWorkspace({ me }: Props) {
  const seniorName = me.profile?.full_name ?? me.profile?.email ?? 'كبير الكتّاب';
  const { selectedClientId } = useProductionClientSelection(me.client_assignments);

  const [queue, setQueue] = useState<QueueState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  // Task 292C2 — the selection lives in the URL, so opening, refreshing and
  // back/forward all preserve the exact draft. `useParams` is undefined when
  // the surface renders as the AppLanding default, which keeps that path on
  // the previous "first row" behaviour.
  const { draftId: routeDraftId } = useParams<{ draftId?: string }>();
  const navigate = useNavigate();
  const selectedId = routeDraftId ?? null;

  const [variant, setVariant] = useState<ContentVariantRow | null>(null);
  // Task 292C2 — the canonical card shows EVERY platform variant of the item,
  // so the reviewer approves what actually ships rather than the single variant
  // this draft happens to target.
  const [allVariants, setAllVariants] = useState<ReadonlyArray<ContentVariantRow>>([]);
  const [inFlight, setInFlight] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [compareError, setCompareError] = useState<string | null>(null);

  // ── Review queue (RLS-scoped to the senior). ──
  useEffect(() => {
    const ctrl = new AbortController();
    setQueue((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    listContentWriterDrafts({ status: 'submitted_to_senior', signal: ctrl.signal })
      .then((data: ListContentWriterDraftsData) => {
        if (ctrl.signal.aborted) return;
        setQueue({ status: 'ready', drafts: data.drafts });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setQueue({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [reloadKey]);

  const unfilteredDrafts = queue.status === 'ready' ? queue.drafts : [];
  const drafts = selectedClientId
    ? unfilteredDrafts.filter((draft) => draft.client_id === selectedClientId)
    : unfilteredDrafts;
  const selected = resolveSelected(drafts, selectedId);
  const selectedItemId = selected?.content_item_id ?? null;
  const selectedVariantId = selected?.content_variant_id ?? null;

  // ── Canonical/AI source variant (for the compare + brief). ──
  useEffect(() => {
    setActionError(null);
    setActionSuccess(null);
    setNotes('');
    setVariant(null);
    setAllVariants([]);
    setCompareError(null);
    if (!selectedItemId || !selectedVariantId) return;
    const ctrl = new AbortController();
    listContentVariants(selectedItemId, { signal: ctrl.signal })
      .then((data: ListContentVariantsData) => {
        if (ctrl.signal.aborted) return;
        setVariant(data.content_variants.find((v) => v.id === selectedVariantId) ?? null);
        setAllVariants(data.content_variants);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || (err instanceof DOMException && err.name === 'AbortError'))
          return;
        setCompareError('تعذّر تحميل المصدر للمقارنة (لا تزال مسودّة الكاتب ظاهرة بالأسفل).');
      });
    return () => ctrl.abort();
  }, [selected?.id, selectedItemId, selectedVariantId]);

  // One clock per render so every row's age label is computed against the same
  // instant (and so the list does not re-render once per minute).
  const now = new Date();

  function reload() {
    setReloadKey((n) => n + 1);
  }

  async function review(decision: 'return' | 'approve_internal') {
    if (inFlight || !selected) return;
    // Task 292C2 — a request-changes reason is mandatory. The RPC refuses a
    // blank one with 23514; refusing here too means the writer never receives
    // an empty rejection and the reviewer is not sent on a doomed round trip.
    if (decision === 'return' && !isValidReviewReason(notes)) {
      setActionError(WRITING_REVIEW_REASON_REQUIRED_AR);
      return;
    }
    // Already reviewed by someone else, or pulled back by the writer.
    if (!isReviewable(selected.status)) {
      setActionError(WRITING_REVIEW_STALE_AR);
      return;
    }
    setInFlight(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const res = await reviewContentWriterDraft({
        input: {
          draft_id: selected.id,
          decision,
          notes: decision === 'return' ? notes.trim() : null,
        },
      });
      setActionSuccess(
        decision === 'return'
          ? 'أُعيدت المسودّة للكاتب مع الملاحظات.'
          : res.sent_to_client
            ? 'اعتُمدت المسودّة وأُرسلت لمراجعة العميل.'
            : 'اعتُمد المحتوى وانتقل إلى المراجعة الداخلية.',
      );
      reload();
    } catch (err) {
      setActionError(actionErrorMessage(err));
    } finally {
      setInFlight(false);
    }
  }

  return (
    <div className="dwk scw" dir="rtl">
      <div className="dwk-top">
        <div className="dwk-greet">
          <h1 className="h-page">
            مراجعة{' '}
            <em className="serif italic" style={{ color: 'var(--tangerine)' }}>
              المحتوى
            </em>
          </h1>
          <p className="dwk-sub">أهلاً {seniorName} — مسودّات المحتوى بانتظار مراجعتك واعتمادها.</p>
        </div>
      </div>

      <div className="dwk-studio">
        {/* REVIEW QUEUE */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              طابور <em>المراجعة</em>
            </span>
            <span className="dwk-ph-c">{queue.status === 'ready' ? drafts.length : SOON}</span>
          </div>
          <div className="dwk-queue">
            {queue.status === 'loading' && (
              <div className="occ-empty sm">{WRITING_REVIEW_LOADING_AR}</div>
            )}
            {queue.status === 'error' && (
              <div className="occ-empty sm">
                تعذّر التحميل.{' '}
                <button type="button" className="dwk-retry" onClick={reload}>
                  إعادة المحاولة
                </button>
              </div>
            )}
            {queue.status === 'ready' && drafts.length === 0 && (
              <div className="occ-empty sm">{writingReviewEmptyAr(me)}</div>
            )}
            {queue.status === 'ready' &&
              drafts.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={`dwk-task${selected?.id === d.id ? ' sel' : ''}`}
                  aria-pressed={selected?.id === d.id}
                  onClick={() => navigate(writingReviewHref(d.id))}
                >
                  {/* Task 292C2 - the row now identifies the WRITER. It used
                      to key the avatar on the client id and show the initial of
                      the item TITLE, so the reviewer could not see who wrote
                      anything. */}
                  <span
                    className="dwk-task-av"
                    style={{ background: avatarColor(d.assignee_user_id) }}
                  >
                    {initial(d.assignee_name ?? d.content_items?.title ?? '؟')}
                  </span>
                  <span className="dwk-task-main">
                    <span className="dwk-task-client">
                      {d.content_items?.title ?? 'عنصر محتوى'}
                    </span>
                    <span className="dwk-task-meta">
                      <span className="dwk-chip">{d.assignee_name ?? 'كاتب غير معروف'}</span>
                      {d.client_name ? <span className="dwk-chip">{d.client_name}</span> : null}
                      {d.content_variants?.platform ? (
                        <span className="dwk-chip">
                          {platformLabelAr(d.content_variants.platform)}
                        </span>
                      ) : null}
                      <span className="dwk-chip soft">{draftStatusLabelAr(d.status)}</span>
                      <span className="dwk-chip soft">{reviewAgeLabelAr(d.submitted_at, now)}</span>
                    </span>
                  </span>
                </button>
              ))}
          </div>
        </section>

        {/* COMPARE: brief | AI/source | writer draft */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              مقارنة <em>المسودّة</em>
            </span>
          </div>
          {!selected ? (
            <div className="occ-empty">اختر مسودّة من الطابور لمراجعتها.</div>
          ) : (
            <div className="dwk-detail">
              <div className="dwk-dt-title">{selected.content_items?.title ?? 'عنصر محتوى'}</div>

              <div className="scw-compare">
                <div className="scw-col">
                  <div className="scw-col-h">المصدر / مسودّة الذكاء الاصطناعي</div>
                  {compareError !== null ? (
                    <div className="occ-empty sm">{compareError}</div>
                  ) : (
                    <DraftText
                      headline={variant?.headline ?? null}
                      caption={variant?.caption ?? null}
                      body={variant?.body ?? null}
                    />
                  )}
                </div>
                <div className="scw-col">
                  <div className="scw-col-h">مسودّة الكاتب</div>
                  <DraftText
                    headline={selected.headline}
                    caption={selected.caption}
                    body={selected.body}
                  />
                </div>
              </div>

              {/* Task 292B2 — every field approval can PROMOTE, canonical vs
                  submitted. Before this, the senior saw only headline/caption/
                  body while approval also wrote hook / on-image text / CTA /
                  hashtags (+ the video keys) into the canonical variant. */}
              <PromotePreview
                canonicalPs={variant?.platform_specific ?? null}
                draftPs={selected.platform_specific}
              />

              {/* Task 292C2 (gap G4) — the CANONICAL internal-review card, the
                  same one DesignerWorkspace and VideoProducerWorkspace render
                  (286F exported it for exactly this). It carries every platform
                  variant, the caption/body, hashtags, core idea, hook and
                  content type an editorial approval needs. Keyed by the item so
                  switching drafts remounts it and resets its internal
                  selected-variant index. The compare + promote panes below stay:
                  they answer "what will this WRITE onto canonical", which is a
                  reviewer question the shared card does not address. */}
              {allVariants.length > 0 && (
                <div className="scw-canonical-card">
                  <IdeaReviewCard
                    key={selectedItemId ?? 'none'}
                    item={reviewItemFromContent({
                      key: selectedItemId ?? 'none',
                      title: selected.content_items?.title ?? 'عنصر محتوى',
                      body: null,
                      variants: allVariants,
                    })}
                    idx={0}
                    mode="saved"
                    mediaSlot={() => (
                      <div className="occ-empty sm">
                        المراجعة هنا نصّية — الأصل يُنتَج بعد اعتماد المحتوى.
                      </div>
                    )}
                  />
                </div>
              )}

              {/* Task 292C2 — an already-reviewed or withdrawn draft is an
                  EXPLICIT state, not a set of buttons that will 400. */}
              {!isReviewable(selected.status) ? (
                <div className="dwk-act-msg err" role="status">
                  {WRITING_REVIEW_STALE_AR}
                </div>
              ) : (
                <>
                  <div className="dwk-actions">
                    <button
                      type="button"
                      className="dwk-act primary"
                      disabled={inFlight}
                      onClick={() => review('approve_internal')}
                    >
                      {WRITING_REVIEW_APPROVE_AR}
                    </button>
                  </div>
                  <div className="scw-blocked-note">{WRITING_REVIEW_APPROVED_NOTE_AR}</div>

                  <div className="dwk-action-form">
                    <label className="dwk-action-label" htmlFor="scw-review-reason">
                      {WRITING_REVIEW_REASON_REQUIRED_AR}
                    </label>
                    <textarea
                      id="scw-review-reason"
                      className="dwk-action-text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="اكتب ما يحتاج الكاتب لتعديله…"
                      rows={3}
                    />
                    <div className="dwk-action-form-btns">
                      <button
                        type="button"
                        className="dwk-act danger"
                        disabled={inFlight || !isValidReviewReason(notes)}
                        onClick={() => review('return')}
                      >
                        {WRITING_REVIEW_REQUEST_CHANGES_AR}
                      </button>
                    </div>
                  </div>
                </>
              )}
              {actionError !== null && <div className="dwk-act-msg err">{actionError}</div>}
              {actionSuccess !== null && <div className="dwk-act-msg ok">{actionSuccess}</div>}
            </div>
          )}
        </section>

        {/* BRIEF */}
        <section className="dwk-pane">
          <div className="dwk-pane-head">
            <span className="dwk-ph-t">
              موجز <em>الإنتاج</em>
            </span>
            <span className="dwk-ph-c">للقراءة فقط</span>
          </div>
          {variant ? (
            <BriefPanel variant={variant} />
          ) : (
            <div className="occ-empty">يظهر الموجز عند اختيار مسودّة.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function DraftText({
  headline,
  caption,
  body,
}: {
  readonly headline: string | null;
  readonly caption: string | null;
  readonly body: string | null;
}) {
  const has = [headline, caption, body].some((x) => typeof x === 'string' && x.trim().length > 0);
  if (!has) return <div className="occ-empty sm">لا يوجد نص.</div>;
  return (
    <div className="scw-text">
      {headline && headline.trim() ? (
        <div className="dwk-dsec">
          <div className="dwk-dsec-t">العنوان</div>
          <div className="dwk-dsec-val">{headline}</div>
        </div>
      ) : null}
      {caption && caption.trim() ? (
        <div className="dwk-dsec">
          <div className="dwk-dsec-t">الكابشن</div>
          <div className="dwk-dsec-val">{caption}</div>
        </div>
      ) : null}
      {body && body.trim() ? (
        <div className="dwk-dsec">
          <div className="dwk-dsec-t">النص</div>
          <div className="dwk-dsec-val">{body}</div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Task 292B2 — the promote preview.
 *
 * `review_content_writer_draft_v1` merges the WRITER-OWNED `platform_specific`
 * keys into the canonical variant on approval. The senior must therefore see
 * each of those fields with the current canonical value beside the submitted
 * one — including fields the writer CLEARED (canonical set, submitted empty),
 * because approving also performs that removal.
 *
 * Agency-only surface: this component is rendered exclusively inside the senior
 * workspace and never on any client-facing route.
 */
function PromotePreview(props: {
  readonly canonicalPs: ContentVariantRow['platform_specific'] | null;
  readonly draftPs: ContentWriterDraftRow['platform_specific'] | null;
}) {
  const rows = promotableComparison(props.canonicalPs, props.draftPs);
  if (rows.length === 0) return null;
  return (
    <div className="scw-promote">
      <div className="scw-col-h">الحقول التي سيعتمدها القرار</div>
      <table className="tbl scw-promote-tbl">
        <thead>
          <tr>
            <th>الحقل</th>
            <th>الحالي (المعتمد)</th>
            <th>المُرسل من الكاتب</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} data-changed={r.changed ? 'true' : undefined}>
              <td>{r.label}</td>
              <td style={{ color: 'var(--muted)' }}>{r.canonical ?? '—'}</td>
              <td>
                {r.effect === 'preserved' ? (
                  <span style={{ color: 'var(--muted)' }}>بدون تغيير</span>
                ) : r.effect === 'cleared' ? (
                  <span className="pill pill-review">سيُحذف</span>
                ) : (
                  r.draft
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BriefPanel({ variant }: { readonly variant: ContentVariantRow }) {
  const staticFields = presentStaticBriefFields(variant.platform_specific);
  const videoFields = isVideoBriefPlatform(variant.platform)
    ? presentVideoBriefFields(variant.platform_specific)
    : [];
  if (staticFields.length === 0 && videoFields.length === 0) {
    return <div className="occ-empty sm">لا يوجد موجز إنتاج.</div>;
  }
  return (
    <div className="dwk-brief">
      {staticFields.map((f) => (
        <div key={f.label} className="dwk-dsec">
          <div className="dwk-dsec-t">{f.label}</div>
          <div className="dwk-dsec-val">{f.value}</div>
        </div>
      ))}
      {videoFields.map((f) => (
        <div key={f.label} className="dwk-dsec">
          <div className="dwk-dsec-t">{f.label}</div>
          <div className="dwk-dsec-val">{f.value}</div>
        </div>
      ))}
    </div>
  );
}
